import React, { useState } from "react";
import { api } from "@/lib/api";
import { COUNTIES } from "@/lib/constants";
import { Shield, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["avocat", "psiholog", "expert", "mediator", "executor", "instituție", "DGASPC", "instanță", "alt profesionist"];

export default function Semnaleaza() {
  const [form, setForm] = useState({
    person_type: "avocat", county: "", city: "", institution_name: "",
    description: "", evidence_url: "", anonymous: true, contact_email: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (form.description.trim().length < 30) { toast.error("Descrierea este prea scurtă"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/tips", form);
      setDone(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 md:py-32 text-center">
        <Shield className="w-14 h-14 text-[#D4A63D] mx-auto mb-6" />
        <h1 className="font-display text-4xl text-[#F8F8F4]">Sesizare înregistrată</h1>
        <p className="mt-5 text-[#A0AABF] leading-relaxed">{done.message}</p>
        <p className="mt-4 text-sm">Codul tău confidențial: <span className="font-mono text-[#D4A63D]">{done.code}</span></p>
        <p className="mt-2 text-xs text-[#6C7A9C]">Notează acest cod. Cu el poți cere actualizări.</p>
      </div>
    );
  }

  return (
    <div data-testid="page-semnaleaza" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
      <header className="mb-10">
        <p className="text-xs tracking-[0.3em] text-[#D4A63D] uppercase mb-4">Confidențial</p>
        <h1 className="font-display text-4xl md:text-5xl text-[#F8F8F4]">Semnalează practici abuzive sau neetice</h1>
        <p className="mt-4 text-[#A0AABF] text-base md:text-lg leading-relaxed">
          Ajută-ne să identificăm tipare și probleme sistemice care afectează copiii și părinții.
        </p>
      </header>

      <div className="cinematic-card rounded-lg p-5 mb-8 border-[#D4A63D]/20 bg-[#D4A63D]/5 flex gap-3">
        <AlertCircle className="w-5 h-5 text-[#D4A63D] shrink-0 mt-0.5" />
        <p className="text-sm text-[#F8F8F4]/85 leading-relaxed">
          Informațiile trimise reprezintă afirmațiile persoanei care le transmite și NU sunt publicate automat.
          Fiecare sesizare este analizată confidențial înainte de orice utilizare editorială.
        </p>
      </div>

      <form onSubmit={submit} className="cinematic-card rounded-lg p-6 md:p-10 space-y-5">
        <div>
          <label className="text-xs uppercase tracking-widest text-[#A0AABF] block mb-2">Tip persoană / instituție *</label>
          <select data-testid="tip-type" value={form.person_type} onChange={e => setForm({ ...form, person_type: e.target.value })} className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-3 text-sm">
            {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <select data-testid="tip-county" value={form.county} onChange={e => setForm({ ...form, county: e.target.value })} className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-3 text-sm">
            <option value="">— Județ (opțional) —</option>
            {COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input data-testid="tip-city" placeholder="Oraș (opțional)" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-3 text-sm" />
        </div>

        <input data-testid="tip-inst" placeholder="Nume instituție / profesionist (opțional)" value={form.institution_name} onChange={e => setForm({ ...form, institution_name: e.target.value })} className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-3 text-sm" />

        <textarea data-testid="tip-desc" required rows={8} placeholder="Descrie pe larg ce s-a întâmplat — date, fapte, context. *" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-3 text-sm resize-y" />

        <input data-testid="tip-evidence" placeholder="Link dovezi (opțional) — Drive, Dropbox, etc." value={form.evidence_url} onChange={e => setForm({ ...form, evidence_url: e.target.value })} className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-3 text-sm" />

        <label className="text-sm text-[#A0AABF] flex items-center gap-2">
          <input type="checkbox" data-testid="tip-anon" checked={form.anonymous} onChange={e => setForm({ ...form, anonymous: e.target.checked })} className="accent-[#D4A63D]" />
          Trimit complet anonim
        </label>

        {!form.anonymous && (
          <input data-testid="tip-contact" type="email" placeholder="Email pentru contact confidențial" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-3 text-sm" />
        )}

        <button data-testid="tip-submit" disabled={loading} className="btn-gold rounded-sm py-4 w-full text-sm uppercase tracking-widest disabled:opacity-50">
          {loading ? "Se trimite..." : "Trimite sesizarea"}
        </button>
      </form>
    </div>
  );
}
